import { useMemo } from 'react'

import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import type {
  Service,
  TakeawayCtaAction,
  TakeawayCreatePostType,
} from '../graphqlEnums'

import type { TakeawayItemCategory } from '../components/Takeaways/types'

/**
 * The clickable takeaway CTAs. Extends the GraphQL CTA actions with three kinds
 * the schema does not name: `viewPost` and `viewPostExternal`, surfaced when a
 * suggested repost cannot be duplicated, and `digIntoInsights`, the header
 * control that opens a channel's own Insights page.
 */
export type TakeawayCtaKind =
  | TakeawayCtaAction
  | 'viewPost'
  | 'viewPostExternal'
  | 'digIntoInsights'

/** Per-card analytics context attached to every takeaway `CTA Clicked` event. */
export type TakeawayCtaTrackingContext = {
  takeawayId: string | null
  channelId: string | null
  /** The channel's network (e.g. instagram, facebook, threads). */
  serviceType: Service | null
  category: TakeawayItemCategory
  cardPosition: number
  /** The createPost media format (e.g. template, reel); only set for createPost takeaways. */
  subCategory?: TakeawayCreatePostType
}

/** Card context plus the clicked button's rendered text, passed in at click time. */
export type TakeawayCtaClickProperties = TakeawayCtaTrackingContext & {
  ctaLabel: string
}

/**
 * Stable cta-string segment for each CTA, reflecting the button's meaning rather
 * than the internal action where they diverge (the repost CTA reads "Duplicate
 * Post"). Kept as a fixed, enumerable set on purpose: some button labels are
 * LLM-generated and would otherwise be unbounded.
 */
const CTA_LABELS: Record<TakeawayCtaKind, string> = {
  createPost: 'createPost',
  publishDraft: 'publishDraft',
  repostPost: 'duplicatePost',
  engageAudience: 'engageAudience',
  adjustPostingGoal: 'adjustGoal',
  adjustPostingSchedule: 'adjustSchedule',
  exploreTemplates: 'exploreTemplates',
  learnMoreAboutFormat: 'learnMore',
  viewPost: 'viewPostDetails',
  viewPostExternal: 'viewPostExternal',
  digIntoInsights: 'digIntoInsights',
}

type TrackTakeawayCta = (properties: TakeawayCtaClickProperties) => void

/**
 * Returns a `CTA Clicked` tracker per takeaway CTA. Each is called from the CTA's
 * click handler with the full context (card props + rendered button label), so
 * nothing has to be baked in when this hook mounts.
 *
 * Fires `BufferTracker.cTAClicked` directly — mirroring the payload `useCta`
 * builds (common tracking props + the CTA id) — because `useCta` bakes its
 * properties at call time and its `clicked` can't carry click-time context. This
 * keeps the shared `useCta` hook untouched.
 */
export function useTrackTakeawayCtaClicked(): Record<
  TakeawayCtaKind,
  TrackTakeawayCta
> {
  const organizationId = useOrganizationId()
  const commonTrackingProperties = useCommonTrackingProperties()

  return useMemo(() => {
    function trackCtaClicked(
      ctaSlug: string,
      properties: TakeawayCtaClickProperties,
    ): void {
      if (!organizationId || !commonTrackingProperties) return
      BufferTracker.cTAClicked({
        clientName: Client.PublishWeb,
        product: Product.Publish,
        cta: `publish-insights-takeaway-${ctaSlug}-1`,
        upgradePathName: null,
        ...commonTrackingProperties,
        ...properties,
      })
    }

    return {
      createPost: (properties): void =>
        trackCtaClicked(CTA_LABELS.createPost, properties),
      publishDraft: (properties): void =>
        trackCtaClicked(CTA_LABELS.publishDraft, properties),
      repostPost: (properties): void =>
        trackCtaClicked(CTA_LABELS.repostPost, properties),
      engageAudience: (properties): void =>
        trackCtaClicked(CTA_LABELS.engageAudience, properties),
      adjustPostingGoal: (properties): void =>
        trackCtaClicked(CTA_LABELS.adjustPostingGoal, properties),
      adjustPostingSchedule: (properties): void =>
        trackCtaClicked(CTA_LABELS.adjustPostingSchedule, properties),
      exploreTemplates: (properties): void =>
        trackCtaClicked(CTA_LABELS.exploreTemplates, properties),
      learnMoreAboutFormat: (properties): void =>
        trackCtaClicked(CTA_LABELS.learnMoreAboutFormat, properties),
      viewPost: (properties): void =>
        trackCtaClicked(CTA_LABELS.viewPost, properties),
      viewPostExternal: (properties): void =>
        trackCtaClicked(CTA_LABELS.viewPostExternal, properties),
      digIntoInsights: (properties): void =>
        trackCtaClicked(CTA_LABELS.digIntoInsights, properties),
    }
  }, [organizationId, commonTrackingProperties])
}
